import { fetchApi } from "../../config/fetchConfig";
import I18n from "../../I18n";
import { buildRequestBodyAsync } from "../../utils";
import { pushDialog } from "./dialogActions";

import dompurify from "dompurify";

const downloadStart = () => ({
    type: "DOWNLOAD_START",
});
const downloadEnd = () => ({
    type: "DOWNLOAD_END",
});

export const downloadAction = (argQueries, locale, cancel) => async (dispatch, getState) => {
    const queries = (argQueries || []).map((q) => {
        return {
            ...q,
            args: {
                ...(q.args || {}),
                page: 0,
                pageSize: 5000,
            },
        };
    });

    // Use async builder to inject DB xlsx labels when xlsxTranslation is present
    let queriesBodies = await buildRequestBodyAsync(queries, locale, true, (url, body, opts) =>
        fetchApi(url, body, opts, { dispatch, user: getState()?.user }),
    );
    queriesBodies.query = (queriesBodies.query || {}).replace("__typename,id,", "");

    dispatch(downloadStart());

    fetchApi(
        "/api/download_xls",
        queriesBodies,
        {
            headers: { "Content-Type": "application/json" },
        },
        { dispatch, user: getState()?.user },
    )
        .then((t) => {
            if (t.status !== 200) {
                dispatch(downloadEnd());
                dispatch(
                    pushDialog("AlertDialog", I18n.translate`XLSX Export Error`, {
                        title: I18n.translate`Something went wrong when generating the requested file`,
                    }),
                ).catch(() => null);
            } else {
                dispatch(downloadEnd());
                cancel && cancel();
                return t.blob().then((b) => {
                    if (b && b.size > 20) {
                        const a = document.createElement("a");
                        const filename = t.headers.get("Filename");

                        a.href = dompurify.sanitize(URL.createObjectURL(b));
                        a.setAttribute("download", filename);
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        dispatch(
                            pushDialog("AlertDialog", I18n.translate`Download Complete`, {
                                title: I18n.translate`File downloaded successfully`,
                            }),
                        ).catch(() => null);
                    } else {
                        console.error("empty file");
                    }
                });
            }
        })
        .catch(() => {
            dispatch(downloadEnd());
            dispatch(
                pushDialog("AlertDialog", I18n.translate`XLSX Export Error`, {
                    title: I18n.translate`Something went wrong when generating the requested file`,
                }),
            ).catch(() => null);
        });
};

export const getDownloadTemplateData =
    (projectId, country, entity, showDialog = false) =>
        (dispatch, getState) => {
            const queriesBody = {
                query: `query($p:String!,$c:String!, $e:String!){get_download_template_data(projectId:$p,country:$c, entity:$e){countries{value,name,taxRegistrationNumber}, entities{value,name}, columns}}`,
                variables: {
                    p: `${projectId}`,
                    c: `${country}`,
                    e: `${entity}`,
                },
            };

            return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
                .then((res) => res.json().catch(() => ({})))
                .then((res) => {
                    const data = res.data["get_download_template_data"] || {};
                    return data;
                })
                .catch((e) => {
                    if (showDialog) {
                        dispatch(
                            pushDialog("AlertDialog", I18n.translate`Request Error`, {
                                title: I18n.translate`Somethings went wrong getting download template data`,
                            }),
                        ).catch(() => null);
                    }
                });
        };

export const downloadCsvStreamAction = (argQueries, locale) => async (dispatch, getState) => {
    // Get the first query's args for the streaming endpoint
    const firstQuery = argQueries?.[0] || {};
    const args = firstQuery.args || {};

    // Get selectedExportableOption if present
    const selectedExportableOption = args.selectedExportableOption || firstQuery.exportableEntity || "";

    dispatch(downloadStart());

    try {
        // Check if browser supports File System Access API
        if (!window.showSaveFilePicker) {
            throw new Error("Your browser does not support this export");
        }

        // Build suggested filename: importId_fiscalYear_entity.csv (TRN will be added by backend)
        const entityName = selectedExportableOption || args.entity || "data";
        const importId = args.importId || "full";
        const fiscalYear = args.fiscalYear || "";
        const suggestedFilename = `${importId}_${fiscalYear}_${entityName}.csv`;

        // 1. Ask user where to save the file
        const fileHandle = await window.showSaveFilePicker({
            suggestedName: suggestedFilename,
            types: [
                {
                    description: "CSV Files",
                    accept: { "text/csv": [".csv"] },
                },
            ],
        });

        // 2. Create writable stream for the file
        const writable = await fileHandle.createWritable();

        // 3. Build query params for the REST streaming endpoint
        const queryParams = new URLSearchParams();
        if (args.entity) queryParams.append("entity", args.entity);
        if (args.projectId) queryParams.append("projectId", args.projectId);
        if (args.fiscalYear) queryParams.append("fiscalYear", args.fiscalYear);
        if (args.importId) queryParams.append("importId", args.importId);
        if (args.modulePrefix) queryParams.append("modulePrefix", args.modulePrefix);
        if (args.organizationId) queryParams.append("organizationId", args.organizationId);
        if (args.filter) queryParams.append("filter", JSON.stringify(args.filter.value || args.filter));
        if (args.sort) queryParams.append("sort", JSON.stringify(args.sort.value || args.sort));
        if (selectedExportableOption) queryParams.append("selectedExportableOption", selectedExportableOption);
        if (args.locale) queryParams.append("locale", args.locale);

        // Use different endpoint for raw_data vs generic views
        // Check if this is a raw_data export by looking at the resource name
        const isRawData = firstQuery.resource?.includes("raw_data");
        const endpoint = isRawData ? "/api/raw_data_stream" : "/api/generic_view_stream";
        const url = `${endpoint}?${queryParams.toString()}`;

        // 4. Use GET request to the streaming endpoint
        const response = await fetchApi(
            url,
            {},
            {
                headers: {
                    "Content-Type": "application/json",
                },
            },
            { dispatch, user: getState()?.user, method: "GET" },
        );

        if (response.status !== 200) {
            await writable.close();
            dispatch(downloadEnd());
            dispatch(
                pushDialog("AlertDialog", I18n.translate`Download Error`, {
                    title: I18n.translate`Something went wrong when downloading the file`,
                }),
            ).catch(() => null);
            return;
        }

        const reader = response.body.getReader();

        let finished = false;
        while (!finished) {
            const { done, value } = await reader.read();
            finished = done;
            if (value) {
                await writable.write(value);
            }
        }

        await writable.close();

        dispatch(downloadEnd());
        dispatch(
            pushDialog("AlertDialog", I18n.translate`Download Complete`, {
                title: I18n.translate`File downloaded successfully`,
            }),
        ).catch(() => null);
    } catch (error) {
        dispatch(downloadEnd());

        // If user canceled the dialog, don't show error
        if (error.name === "AbortError") {
            dispatch(
                pushDialog("AlertDialog", I18n.translate`Download Error`, {
                    title: I18n.translate`Download canceled by user`,
                }),
            ).catch(() => null);
            return;
        }

        dispatch(
            pushDialog("AlertDialog", I18n.translate`Download Error`, {
                title: I18n.translate`Something went wrong when downloading the file: ${error}`,
            }),
        ).catch(() => null);
    }
};
